import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '@/context/LanguageContext';
import LanguageSwitcher from '@/components/LanguageSwitcher';
import { TrendUp, ArrowRight, Eye, Tag, MagnifyingGlass, X } from '@phosphor-icons/react';
import Logo from '@/components/Logo';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BlogListPage = () => {
  const { t, language } = useLanguage();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [activeTag, setActiveTag] = useState(null);

  useEffect(() => {
    document.title = language === 'tr'
      ? 'Blog — GrowthRIQ | E-Ticaret ve E-İhracat Rehberleri'
      : 'Blog — GrowthRIQ | E-Commerce and E-Export Guides';
    const desc = language === 'tr'
      ? 'E-ticaret, e-ihracat, SWOT, Amazon satıcılığı ve büyüme stratejileri hakkında uzman rehberler.'
      : 'Expert guides on e-commerce, e-export, SWOT, Amazon selling, and growth strategies.';
    let el = document.head.querySelector('meta[name="description"]');
    if (el) el.setAttribute('content', desc);
    document.documentElement.setAttribute('lang', language);
  }, [language]);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API_URL}/blog?lang=${language}`)
      .then(res => setPosts(res.data))
      .finally(() => setLoading(false));
  }, [language]);

  const prefix = language === 'en' ? '/en' : '';

  // Compute available tags + filtered posts
  const allTags = useMemo(() => {
    const counts = new Map();
    posts.forEach((p) => (p.tags || []).forEach((t) => counts.set(t, (counts.get(t) || 0) + 1)));
    return Array.from(counts.entries()).sort((a, b) => b[1] - a[1]).slice(0, 10);
  }, [posts]);

  const filteredPosts = useMemo(() => {
    const q = query.trim().toLowerCase();
    return posts.filter((p) => {
      if (activeTag && !(p.tags || []).includes(activeTag)) return false;
      if (!q) return true;
      const hay = `${p.title || ''} ${p.excerpt || ''} ${(p.tags || []).join(' ')}`.toLowerCase();
      return hay.includes(q);
    });
  }, [posts, query, activeTag]);

  return (
    <div className="min-h-screen bg-white">
      {/* Nav */}
      <nav className="border-b border-[#E4E4E7] sticky top-0 bg-white/95 backdrop-blur z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to={`${prefix}/`} className="flex items-center gap-2" data-testid="nav-logo">
            <Logo variant="light" size="md" />
          </Link>
          <div className="flex items-center gap-4">
            <LanguageSwitcher />
            <Link to={`${prefix}/pricing`} className="text-[#52525B] hover:text-[#09090B] font-medium hidden sm:inline" data-testid="nav-pricing">
              {t('nav.pricing')}
            </Link>
            <Link to={`${prefix}/register`} className="btn-primary" data-testid="nav-register">
              {t('nav.register')}
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="py-16 px-6 border-b border-[#E4E4E7] bg-[#F4F4F5]">
        <div className="max-w-7xl mx-auto">
          <p className="label-text text-[#0F1B36] mb-4" data-testid="blog-label">
            {language === 'tr' ? 'BLOG' : 'BLOG'}
          </p>
          <h1 className="heading-1 text-[#09090B] mb-4" data-testid="blog-title">
            {language === 'tr' ? 'E-Ticaret & E-İhracat Rehberleri' : 'E-Commerce & E-Export Guides'}
          </h1>
          <p className="text-xl text-[#52525B] max-w-2xl">
            {language === 'tr'
              ? 'Stratejik büyüme, pazar araştırması ve operasyonel verim için uzman içerikler.'
              : 'Expert content on strategic growth, market research, and operational efficiency.'}
          </p>
        </div>
      </section>

      {/* Posts grid */}
      <section className="py-16 px-6">
        <div className="max-w-7xl mx-auto">
          {/* Search + tag filter bar */}
          <div className="mb-10 flex flex-col lg:flex-row lg:items-center gap-4" data-testid="blog-filter-bar">
            <div className="relative flex-1 max-w-md">
              <MagnifyingGlass size={16} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-[#71717A]" />
              <input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={language === 'tr' ? 'Yazılarda ara…' : 'Search posts…'}
                className="w-full pl-9 pr-9 py-2.5 border border-[#E4E4E7] focus:border-[#0F1B36] focus:outline-none text-sm bg-white"
                data-testid="blog-search-input"
                aria-label={language === 'tr' ? 'Blog araması' : 'Blog search'}
              />
              {query && (
                <button
                  type="button"
                  onClick={() => setQuery('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#71717A] hover:text-[#09090B]"
                  data-testid="blog-search-clear"
                  aria-label={language === 'tr' ? 'Aramayı temizle' : 'Clear search'}
                >
                  <X size={14} weight="bold" />
                </button>
              )}
            </div>
            <div className="flex items-center gap-2 flex-wrap flex-1" data-testid="blog-tag-filters">
              <button
                type="button"
                onClick={() => setActiveTag(null)}
                className={`text-xs px-3 py-1.5 uppercase tracking-wide transition-colors ${activeTag === null ? 'bg-[#0F1B36] text-white' : 'bg-[#F4F4F5] text-[#52525B] hover:bg-[#E4E4E7]'}`}
                data-testid="blog-tag-all"
              >
                {language === 'tr' ? 'Tümü' : 'All'} · {posts.length}
              </button>
              {allTags.map(([tag, count]) => (
                <button
                  key={tag}
                  type="button"
                  onClick={() => setActiveTag(activeTag === tag ? null : tag)}
                  className={`text-xs px-3 py-1.5 uppercase tracking-wide transition-colors ${activeTag === tag ? 'bg-[#0F1B36] text-white' : 'bg-[#F4F4F5] text-[#52525B] hover:bg-[#E4E4E7]'}`}
                  data-testid={`blog-tag-${tag}`}
                >
                  {tag} · {count}
                </button>
              ))}
            </div>
          </div>

          {loading ? (
            <div className="text-[#52525B]" data-testid="blog-loading">Loading…</div>
          ) : filteredPosts.length === 0 ? (
            <div className="text-[#52525B] py-12 text-center" data-testid="blog-empty">
              {language === 'tr'
                ? (posts.length === 0 ? 'Henüz yazı yok.' : 'Aramanıza uygun yazı bulunamadı.')
                : (posts.length === 0 ? 'No posts yet.' : 'No posts match your search.')}
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="blog-grid">
              {filteredPosts.map((post, i) => (
                <Link
                  key={post.slug}
                  to={`${prefix}/blog/${post.slug}`}
                  className="group border border-[#E4E4E7] bg-white hover:border-[#0F1B36] transition-colors"
                  data-testid={`blog-card-${i}`}
                >
                  {post.cover_image && (
                    <div className="aspect-video bg-[#F4F4F5] overflow-hidden">
                      <img
                        src={post.cover_image}
                        alt={post.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        loading={i < 3 ? 'eager' : 'lazy'}
                        decoding="async"
                        width="640"
                        height="360"
                      />
                    </div>
                  )}
                  <div className="p-6">
                    <div className="flex items-center gap-2 flex-wrap mb-3">
                      {(post.tags || []).slice(0, 2).map(tag => (
                        <span key={tag} className="text-xs px-2 py-1 bg-[#F4F4F5] text-[#52525B] uppercase tracking-wide">
                          <Tag size={10} weight="bold" className="inline mr-1" />
                          {tag}
                        </span>
                      ))}
                    </div>
                    <h2 className="text-xl font-bold text-[#09090B] mb-2 group-hover:text-[#0F1B36] transition-colors line-clamp-2">
                      {post.title}
                    </h2>
                    <p className="text-[#52525B] text-sm line-clamp-3 mb-4">{post.excerpt}</p>
                    <div className="flex items-center justify-between text-xs text-[#71717A]">
                      <span>{post.author}</span>
                      <span className="flex items-center gap-1">
                        <Eye size={12} weight="bold" /> {post.views}
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-6 bg-[#09090B]" data-testid="blog-cta">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="heading-2 text-white mb-4">
            {language === 'tr'
              ? 'Okumak yetmez — kendi stratejinizi ölçün'
              : 'Reading isn\'t enough — measure your own strategy'}
          </h2>
          <p className="text-[#A1A1AA] mb-8">
            {language === 'tr'
              ? 'İlk değerlendirme ücretsiz. 10 kategoride AI destekli skor + yol haritası.'
              : 'First assessment is free. AI-driven score across 10 categories + a roadmap.'}
          </p>
          <Link to={`${prefix}/register`} className="btn-primary inline-flex items-center gap-2" data-testid="blog-cta-register">
            {language === 'tr' ? 'Ücretsiz Başla' : 'Start Free'}
            <ArrowRight size={20} weight="bold" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default BlogListPage;
